/**
 * Matrice fit culturel d'une campagne : un candidat par ligne, un item du
 * référentiel par colonne, le SCORE de niveau en cellule (7 = Essentiel sur la
 * pyramide à 7 niveaux) — le même chiffre que la fiche du candidat et le rapport.
 *
 * Même patron que `PamiCampagneTable` : `DataTable` en largeurs `auto`, colonne
 * Candidat figée. Pas de pied de tableau ici — une moyenne de rangs ipsatifs ne
 * veut rien dire, chaque candidat ayant distribué les mêmes niveaux.
 */
import {
  Card,
  CardContent,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  filtreRecherche,
  type ColonneDataTable,
  type SortingState,
} from "@repo/ui";
import { dateCourte, lectureNiveauQsort } from "@repo/utils";
import { useMemo, useState } from "react";

/** Une passation fit-culturel, telle que renvoyée par
 *  `obtenirEvaluationsFitCulturelParCampagne`. Le NIVEAU est brut : 0 = haut de
 *  la pyramide, l'item le plus important pour le candidat. Il ne s'affiche
 *  jamais tel quel (cf. `lectureNiveauQsort`). */
export type FitCulturelEvalRow = {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  niveaux: { code: string; niveau: number }[];
};

type Props = {
  evaluations: FitCulturelEvalRow[];
  /**
   * Items du référentiel fit-culturel de la campagne, dans l'ordre de
   * configuration : ils donnent les colonnes ET leur libellé. Absents (aucun
   * référentiel encore configuré), les colonnes se déduisent des passations et
   * n'affichent que le code.
   */
  dimensions?: { code: string; label: string }[];
  /** Hauteur de la pyramide de la campagne (7, ou 5 pour une campagne
   *  configurée avant le figement à 23 items). Absente, elle se déduit des
   *  passations : sous distribution forcée, chaque niveau est rempli. */
  nbNiveaux?: number;
  /** Lien de chaque ligne vers l'évaluation du candidat. */
  hrefLigne?: (ligne: FitCulturelEvalRow) => string;
};

/** Ordre par défaut : les passations les plus récentes d'abord. */
const TRI_INITIAL: SortingState = [{ id: "date", desc: true }];

const RECHERCHE = {
  colonne: "candidat",
  placeholder: "Nom, prénom…",
  libelle: "Rechercher un candidat",
} as const;

const BARRE_FILTRES = { recherche: RECHERCHE };

/**
 * Dégradé du score : le plus important (score `nbNiveaux`) est le plus soutenu.
 * Une seule teinte, pas une palette rouge↔vert — un classement ipsatif n'a ni bon
 * ni mauvais pôle, seulement un ordre de priorité.
 */
function couleurScore(score: number, nbNiveaux: number): string {
  const part = (score - 1) / Math.max(1, nbNiveaux - 1);
  return `color-mix(in srgb, var(--primary) ${Math.round(20 + part * 70)}%, transparent)`;
}

function nomCandidat(ligne: FitCulturelEvalRow): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

/** Niveau d'un item pour une passation, `undefined` s'il n'a pas été classé. */
function niveauDe(ligne: FitCulturelEvalRow, code: string): number | undefined {
  return ligne.niveaux.find((n) => n.code === code)?.niveau;
}

export function FitCulturelCampagneTable({
  evaluations,
  dimensions,
  nbNiveaux: nbNiveauxConfig,
  hrefLigne,
}: Props) {
  // Tri interne : la table n'a pas d'URL à elle, et l'ordre par défaut
  // (dernières passations d'abord) doit survivre au tri manuel de l'utilisateur.
  const [tri, setTri] = useState<SortingState>(TRI_INITIAL);

  // Plus de valeur par défaut à 5 : elle donnait « niveau 0 sur 5 » sur une
  // pyramide à 7 niveaux. Déduite des passations, la hauteur est exacte dès
  // qu'une passation complète est là.
  const nbNiveaux = useMemo(
    () =>
      nbNiveauxConfig ??
      evaluations.reduce(
        (max, e) => Math.max(max, ...e.niveaux.map((n) => n.niveau + 1)),
        1,
      ),
    [nbNiveauxConfig, evaluations],
  );

  const items = useMemo(
    () =>
      dimensions && dimensions.length > 0
        ? dimensions
        : [
            ...new Set(evaluations.flatMap((e) => e.niveaux.map((n) => n.code))),
          ].map((code) => ({ code, label: code })),
    [dimensions, evaluations],
  );

  const colonnes = useMemo<ColonneDataTable<FitCulturelEvalRow>[]>(
    () => [
      {
        id: "candidat",
        header: "Candidat",
        accessorFn: nomCandidat,
        cell: ({ row }) => {
          const nom = nomCandidat(row.original);
          return nom ? (
            <span className="font-medium whitespace-nowrap">{nom}</span>
          ) : (
            <span className="text-muted-foreground italic">Anonyme</span>
          );
        },
        enableHiding: false,
        filterFn: filtreRecherche<FitCulturelEvalRow>((l) => [
          l.prenomCandidat,
          l.nomCandidat,
        ]),
        meta: { libelle: "Candidat", largeur: "12rem", figee: true },
      },
      {
        id: "date",
        header: "Date",
        accessorFn: (l) => l.dateFin ?? 0,
        cell: ({ row }) => (
          <span className="data whitespace-nowrap text-muted-foreground">
            {row.original.dateFin ? dateCourte(row.original.dateFin) : "—"}
          </span>
        ),
        meta: { libelle: "Date", largeur: "7rem" },
      },
      ...items.map<ColonneDataTable<FitCulturelEvalRow>>((item) => ({
        id: item.code,
        // `asChild` sur un <span> : l'en-tête triable est déjà un <button>, et
        // un bouton dans un bouton est du HTML invalide.
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default block max-w-24 truncate">
                {item.label}
              </span>
            </TooltipTrigger>
            <TooltipContent>{item.label}</TooltipContent>
          </Tooltip>
        ),
        // `null` plutôt qu'`undefined` pour un item non classé : TanStack range
        // les valeurs nulles en fin de tri au lieu de les mêler aux scores. Tri
        // sur le score affiché, pas sur le niveau brut, qui va en sens inverse.
        accessorFn: (l) => {
          const niveau = niveauDe(l, item.code);
          return niveau === undefined ? null : nbNiveaux - niveau;
        },
        cell: ({ row }) => {
          const niveau = niveauDe(row.original, item.code);
          if (niveau === undefined) {
            return <span className="text-muted-foreground">—</span>;
          }
          const { score, libelle } = lectureNiveauQsort(niveau, nbNiveaux);
          return (
            <span
              className="inline-block rounded-pastille px-2 py-0.5 text-xs font-semibold tabular-nums"
              style={{ backgroundColor: couleurScore(score, nbNiveaux) }}
              // Le chiffre seul est muet : l'infobulle dit le niveau en mots.
              title={`${score} sur ${nbNiveaux} · ${libelle}`}
            >
              {score}
            </span>
          );
        },
        meta: { libelle: item.label, largeur: "5.5rem" },
      })),
    ],
    [items, nbNiveaux],
  );

  if (evaluations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucune passation PC Fit terminée dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  const libelles = [
    lectureNiveauQsort(0, nbNiveaux).libelle,
    lectureNiveauQsort(nbNiveaux - 1, nbNiveaux).libelle,
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {nbNiveaux} = {libelles[0]} · 1 = {libelles[1]}
      </p>
      <DataTable
        colonnes={colonnes}
        donnees={evaluations}
        cleColonnes="campagne-fit-culturel"
        barreFiltres={BARRE_FILTRES}
        tri={tri}
        onTriChange={setTri}
        hrefLigne={hrefLigne}
        // La comparaison entre candidats est tout l'objet de cette table : la
        // paginer la couperait en deux.
        taillePage="toutes"
        largeurColonnes="auto"
        densite="compacte"
        videFiltre={{ titre: "Aucun candidat pour cette recherche" }}
      />
    </div>
  );
}
